import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getPublicTenant, getPublicSlots, createPublicBooking, getPublicIntakeForm, joinWaitlist } from '../../services/api';
import { safeParseJSON, applyTenantBranding, clearTenantBranding } from '../../context/AuthContext';
import { formatDateTimeLong } from '../../utils/formatDateTime';
import PhoneInput from '../../components/PhoneInput';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function addDays(d, n) {
  const r = new Date(d); r.setDate(r.getDate() + n); return r;
}

function toYMD(d) {
  return d.toISOString().split('T')[0];
}

function buildNextDays(openDays, count = 30) {
  const days = [];
  let cursor = new Date();
  cursor.setHours(0,0,0,0);
  while (days.length < count) {
    if (!openDays.length || openDays.includes(cursor.getDay())) {
      days.push(new Date(cursor));
    }
    cursor = addDays(cursor, 1);
    if (days.length > 60) break;
  }
  return days.slice(0, 30);
}

function fmtDay(d) {
  const base = d.toLocaleDateString('pt-BR', { weekday:'short', day:'2-digit', month:'short' });
  const today = new Date(); today.setHours(0,0,0,0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const target = new Date(d); target.setHours(0,0,0,0);
  if (target.getTime() === today.getTime()) return `Hoje · ${base}`;
  if (target.getTime() === tomorrow.getTime()) return `Amanhã · ${base}`;
  return base;
}

function fmtPrice(v) {
  const n = Number(v);
  if (!n) return 'Gratuito';
  return n.toLocaleString('pt-BR', { style:'currency', currency:'BRL' });
}

// ─── Steps ────────────────────────────────────────────────────────────────────

const STEPS = ['Serviço', 'Data', 'Horário', 'Seus dados', 'Confirmação'];

function StepIndicator({ step }) {
  return (
    <div style={{ display:'flex', justifyContent:'center', gap:0, marginBottom:32 }}>
      {STEPS.map((label, i) => (
        <div key={i} style={{ display:'flex', alignItems:'center' }}>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
            <div style={{
              width:32, height:32, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
              background: i <= step ? 'var(--primary)' : 'var(--gray-200)',
              color: i <= step ? '#fff' : 'var(--gray-400)',
              fontSize:14, fontWeight:600, transition:'all .2s',
            }}>
              {i < step ? '✓' : i + 1}
            </div>
            <span style={{ fontSize:11, color: i <= step ? 'var(--primary)' : 'var(--gray-400)', fontWeight: i === step ? 600 : 400, whiteSpace:'nowrap' }}>
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{ width:48, height:2, background: i < step ? 'var(--primary)' : 'var(--gray-200)', marginTop:-18, transition:'all .2s' }} />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function PublicBooking() {
  const { slug } = useParams();
  const [tenantData, setTenantData] = useState(null);
  const [error,      setError]      = useState(null);
  const [loading,    setLoading]    = useState(true);

  const [step,         setStep]         = useState(0);
  const [selectedSvc,  setSelectedSvc]  = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [slots,        setSlots]        = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const [form, setForm] = useState({ client_name:'', client_email:'', client_contact:'', notes:'' });
  const [submitting, setSubmitting] = useState(false);
  const [booking,    setBooking]    = useState(null);

  // Intake form fields
  const [intakeFields,   setIntakeFields]   = useState([]);
  const [intakeAnswers,  setIntakeAnswers]  = useState({});

  // Waitlist
  const [showWaitlist,   setShowWaitlist]   = useState(false);
  const [wlForm,         setWlForm]         = useState({ client_name:'', client_phone:'', client_email:'', preferred_date:'' });
  const [wlSubmitting,   setWlSubmitting]   = useState(false);
  const [wlDone,         setWlDone]         = useState(false);

  // Apply tenant branding (primary + variação dark gerada automaticamente)
  useEffect(() => {
    if (tenantData?.tenant?.primary_color) {
      applyTenantBranding(tenantData.tenant.primary_color);
    }
    return () => clearTenantBranding();
  }, [tenantData]);

  useEffect(() => {
    setLoading(true);
    getPublicTenant(slug)
      .then(r => setTenantData(r.data))
      .catch(e => setError(e.response?.data?.error || 'Página de agendamento não encontrada'))
      .finally(() => setLoading(false));
  }, [slug]);

  const loadSlots = useCallback(async (date, svcId) => {
    setSlotsLoading(true);
    setSlots([]);
    try {
      const r = await getPublicSlots(slug, date, svcId);
      setSlots(r.data.slots || []);
    } catch { setSlots([]); } finally { setSlotsLoading(false); }
  }, [slug]);

  const selectService = svc => {
    setSelectedSvc(svc); setSelectedDate(null); setSelectedTime(null); setIntakeAnswers({});
    setStep(1);
    // Load intake form fields for this service
    getPublicIntakeForm(slug, svc.id).then(r => setIntakeFields(r.data.fields || [])).catch(() => setIntakeFields([]));
  };
  const selectDate    = date => {
    const dateStr = toYMD(date);
    setSelectedDate(dateStr);
    setSelectedTime(null);
    setStep(2);
    loadSlots(dateStr, selectedSvc?.id);
  };
  const selectTime = time => { setSelectedTime(time); setStep(3); };

  const onSubmit = async e => {
    e.preventDefault();
    if (!form.client_name.trim()) { toast.error('Informe seu nome completo.'); return; }
    // Validate required intake fields
    for (const f of intakeFields) {
      if (f.required && !intakeAnswers[f.id]) { toast.error(`Campo obrigatório: ${f.label}`); return; }
    }
    setSubmitting(true);
    try {
      const intakeResponses = Object.entries(intakeAnswers).map(([field_id, value]) => ({ field_id: parseInt(field_id), value }));
      const r = await createPublicBooking(slug, {
        ...form,
        service_id: selectedSvc?.id || null,
        date: selectedDate,
        time: selectedTime,
        intake_responses: intakeResponses,
      });
      // Se agendamento exige sinal PIX, guardamos os dados do deposit
      // pra mostrar QR na tela final. Se não exige, deposit vem null.
      setBooking({ ...r.data.appointment, deposit: r.data.deposit || null });
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao criar agendamento. Tente novamente.');
    } finally { setSubmitting(false); }
  };

  const onJoinWaitlist = async e => {
    e.preventDefault();
    if (!wlForm.client_name.trim() || !wlForm.client_phone.trim()) { toast.error('Nome e telefone são obrigatórios'); return; }
    setWlSubmitting(true);
    try {
      await joinWaitlist(slug, { ...wlForm, service_id: selectedSvc?.id });
      setWlDone(true);
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao entrar na lista'); }
    finally { setWlSubmitting(false); }
  };

  const onChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  // ── Loading / Error states ─────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'100vh', background:'var(--page-bg)' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight:'100vh', background:'var(--page-bg)', display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', padding:24 }}>
        <div style={{ background:'var(--surface)', borderRadius:16, padding:'40px 32px', maxWidth:400, width:'100%', textAlign:'center', boxShadow:'0 4px 24px rgba(0,0,0,.08)' }}>
          <div style={{ fontSize:56, marginBottom:16 }}>📅</div>
          <h2 style={{ margin:'0 0 8px', color:'var(--gray-900)', fontSize:22 }}>Página não encontrada</h2>
          <p style={{ color:'var(--gray-500)', fontSize:14, lineHeight:1.5, marginBottom:0 }}>{error}</p>
        </div>
        <div style={{ marginTop:24, color:'var(--gray-400)', fontSize:12 }}>
          Powered by <strong style={{ color:'var(--gray-500)' }}>AtendoApp</strong>
        </div>
      </div>
    );
  }

  const { tenant, services, open_days } = tenantData;
  const availableDays = buildNextDays(open_days || []);

  // ── Step 4: Success ────────────────────────────────────────────────────────
  if (step === 4 && booking) {
    const dt = formatDateTimeLong(booking.datetime);
    const hasDeposit = booking.deposit && !booking.deposit.error && booking.deposit.pix_code;
    const depositError = booking.deposit?.error;
    return (
      <div style={{ minHeight:'100vh', background:'var(--page-bg)', display:'flex', justifyContent:'center', alignItems:'center', padding:16 }}>
        <div style={{ background:'var(--surface)', borderRadius:16, padding:'36px 28px', maxWidth:460, width:'100%', textAlign:'center', boxShadow:'0 4px 24px rgba(0,0,0,.08)' }}>
          {/* Ícone: sucesso quando sem sinal, PIX pendente quando tem */}
          <div style={{
            width:72, height:72, borderRadius:'50%',
            background: hasDeposit ? '#fef3c7' : '#dcfce7',
            display:'flex', alignItems:'center', justifyContent:'center',
            margin:'0 auto 16px', fontSize:36,
          }}>
            {hasDeposit ? '💳' : '✅'}
          </div>
          <h2 style={{ margin:'0 0 6px', color:'var(--gray-900)', fontSize:22 }}>
            {hasDeposit ? 'Pague o sinal pra confirmar' : 'Agendamento confirmado!'}
          </h2>
          <p style={{ color:'var(--gray-500)', marginBottom:20, fontSize:14 }}>
            {hasDeposit
              ? 'Seu horário está reservado. Pague o sinal abaixo pra efetivar.'
              : 'Tudo certo. Veja os detalhes abaixo.'}
          </p>

          {/* Card de resumo */}
          <div style={{ background:'var(--gray-50)', borderRadius:12, padding:'18px 20px', textAlign:'left', marginBottom:20, border:'1px solid var(--gray-200)' }}>
            {booking.service_name && <Row label="Serviço" value={booking.service_name} />}
            <Row label="Data e horário" value={dt} />
            <Row label="Duração" value={`${booking.duration_minutes} min`} />
            {tenant?.name && <Row label="Local" value={tenant.name} />}
          </div>

          {/* ── Bloco de PIX (quando aplicável) ── */}
          {hasDeposit && (
            <div style={{
              background:'#fff7ed', border:'1px solid #fdba74', borderRadius:12,
              padding:'20px 18px', marginBottom:20, textAlign:'center',
            }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#9a3412', textTransform:'uppercase', letterSpacing:.8, marginBottom:6 }}>
                Sinal a pagar via PIX
              </div>
              <div style={{ fontSize:32, fontWeight:800, color:'#9a3412', marginBottom:14 }}>
                R$ {Number(booking.deposit.amount).toFixed(2).replace('.', ',')}
              </div>

              {/* QR code via serviço externo (mesmo usado pro QR de agendamento) */}
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(booking.deposit.pix_code)}`}
                alt="QR code PIX"
                style={{ width:240, height:240, borderRadius:8, border:'2px solid #fff', background:'#fff', marginBottom:12 }}
              />

              <div style={{ fontSize:12, color:'var(--gray-600)', marginBottom:10 }}>
                Aponte a câmera do app do seu banco ou copie o código abaixo
              </div>

              <div style={{
                background:'#fff', border:'1px solid var(--gray-200)', borderRadius:8,
                padding:'10px 12px', fontSize:11, fontFamily:'monospace',
                wordBreak:'break-all', textAlign:'left', marginBottom:10, color:'var(--gray-700)',
              }}>
                {booking.deposit.pix_code}
              </div>

              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(booking.deposit.pix_code);
                  toast.success('Código PIX copiado!');
                }}
                style={{
                  background:'#ea580c', color:'#fff', border:'none', borderRadius:8,
                  padding:'10px 20px', fontSize:14, fontWeight:700, cursor:'pointer',
                  width:'100%',
                }}
              >
                📋 Copiar código PIX
              </button>

              {booking.deposit.expires_at && (
                <div style={{ fontSize:11, color:'var(--gray-500)', marginTop:10 }}>
                  ⏳ Expira em {new Date(booking.deposit.expires_at).toLocaleString('pt-BR')}
                </div>
              )}
            </div>
          )}

          {/* Erro ao gerar PIX (tenant sem chave configurada etc) */}
          {depositError && (
            <div style={{
              background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10,
              padding:'12px 16px', marginBottom:20, fontSize:13, color:'#991b1b', textAlign:'left',
            }}>
              ⚠️ {depositError}
            </div>
          )}

          {/* Dica de email */}
          <div style={{ background:'#eff6ff', borderRadius:10, padding:'12px 16px', marginBottom:20, fontSize:13, color:'#1e40af', textAlign:'left', border:'1px solid #bfdbfe' }}>
            📧 Se informou seu email, você receberá uma confirmação com opções para cancelar ou reagendar.
          </div>

          {/* Ações */}
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <button style={{ background:'var(--primary)', color:'#fff', border:'none', borderRadius:10, padding:'12px 24px', fontSize:15, fontWeight:700, cursor:'pointer' }}
              onClick={() => { setStep(0); setSelectedSvc(null); setSelectedDate(null); setSelectedTime(null); setBooking(null); }}>
              Fazer outro agendamento
            </button>
            {booking?.token && (
              <>
                <Link to={`/agendar/${slug}/cancelar/${booking.token}`}
                  style={{ fontSize:13, color:'var(--gray-400)', textDecoration:'none', textAlign:'center' }}>
                  Precisa cancelar?
                </Link>
                {booking?.reschedule_token && (
                  <Link to={`/reagendar/${slug}/${booking.reschedule_token}`}
                    style={{ fontSize:13, color:'var(--gray-400)', textDecoration:'none', textAlign:'center' }}>
                    Precisa reagendar?
                  </Link>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight:'100vh', background:'var(--page-bg)' }}>
      {/* Header */}
      <div style={{ background:'var(--primary)', color:'#fff', padding:'20px 24px', display:'flex', alignItems:'center', gap:16 }}>
        {tenant.logo_url
          ? <img src={tenant.logo_url} alt="logo" style={{ height:48, objectFit:'contain', borderRadius:8, background:'rgba(255,255,255,.15)', padding:4 }} />
          : <div style={{ width:48, height:48, borderRadius:12, background:'rgba(255,255,255,.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, fontWeight:700 }}>{tenant.name[0]}</div>
        }
        <div>
          <h1 style={{ margin:0, fontSize:20, fontWeight:700 }}>{tenant.name}</h1>
          <p style={{ margin:0, fontSize:13, opacity:.85 }}>Agendamento Online</p>
        </div>
      </div>

      {/* Trust bar */}
      <div style={{ background:'var(--surface)', borderBottom:'1px solid var(--gray-200)', padding:'10px 16px', display:'flex', justifyContent:'center', gap:20, flexWrap:'wrap', fontSize:12, color:'var(--gray-500)' }}>
        <span>🔒 Dados seguros</span>
        <span>❌ Cancelamento grátis</span>
        <span>⚡ Sem cadastro</span>
      </div>

      {/* Body */}
      <div style={{ maxWidth:680, margin:'0 auto', padding:'32px 16px' }}>
        <StepIndicator step={step} />

        {/* Step 0: Escolher serviço */}
        {step === 0 && (
          <div>
            <h2 style={{ marginBottom:20, fontSize:18, color:'var(--gray-900)' }}>Qual serviço você deseja agendar?</h2>
            {!services.length ? (
              <div style={{ textAlign:'center', padding:40, color:'var(--gray-500)' }}>
                <div style={{ fontSize:40, marginBottom:12 }}>🛎️</div>
                <p>Nenhum serviço disponível no momento.</p>
              </div>
            ) : (
              <div style={{ display:'grid', gap:12 }}>
                {services.map(svc => (
                  <button key={svc.id} onClick={() => selectService(svc)}
                    style={{ background:'var(--surface)', border:`2px solid var(--gray-200)`, borderRadius:12, padding:'16px 20px', cursor:'pointer', textAlign:'left', display:'flex', justifyContent:'space-between', alignItems:'center', transition:'all .15s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor='var(--primary)'; e.currentTarget.style.boxShadow='0 0 0 3px rgba(37,99,235,.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor='var(--gray-200)'; e.currentTarget.style.boxShadow='none'; }}>
                    <div>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                        <div style={{ width:10, height:10, borderRadius:'50%', background:svc.color||'var(--primary)' }} />
                        <strong style={{ fontSize:15, color:'var(--gray-900)' }}>{svc.name}</strong>
                      </div>
                      {svc.description && <p style={{ margin:'0 0 4px', fontSize:13, color:'var(--gray-500)' }}>{svc.description}</p>}
                      <span style={{ fontSize:13, color:'var(--gray-700)' }}>⏱ {svc.duration_minutes} min</span>
                    </div>
                    <div style={{ textAlign:'right', flexShrink:0, marginLeft:12 }}>
                      <div style={{ fontSize:15, fontWeight:600, color:'var(--primary)' }}>{fmtPrice(svc.price)}</div>
                      <div style={{ fontSize:12, color:'var(--gray-400)', marginTop:2 }}>→</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 1: Escolher data */}
        {step === 1 && (
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
              <button onClick={() => setStep(0)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:'var(--gray-500)' }}>←</button>
              <h2 style={{ margin:0, fontSize:18, color:'var(--gray-900)' }}>Escolha a data</h2>
            </div>
            {selectedSvc && (
              <div className="info-box" style={{ marginBottom:20, fontSize:13 }}>
                <span>🛎️</span><span><strong>{selectedSvc.name}</strong> · {selectedSvc.duration_minutes} min · {fmtPrice(selectedSvc.price)}</span>
              </div>
            )}
            {!availableDays.length ? (
              <p style={{ color:'var(--gray-500)' }}>Nenhum dia disponível nos próximos 30 dias.</p>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:8 }}>
                {availableDays.map((d, i) => (
                  <button key={i} onClick={() => selectDate(d)}
                    style={{ background:'var(--surface)', border:'2px solid var(--gray-200)', borderRadius:10, padding:'12px 8px', cursor:'pointer', textAlign:'center', fontSize:13, fontWeight:500, color:'var(--gray-800)', transition:'all .15s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor='var(--primary)'; e.currentTarget.style.background='var(--gray-50)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor='var(--gray-200)'; e.currentTarget.style.background='var(--surface)'; }}>
                    {fmtDay(d)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Escolher horário */}
        {step === 2 && (
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
              <button onClick={() => setStep(1)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:'var(--gray-500)' }}>←</button>
              <h2 style={{ margin:0, fontSize:18, color:'var(--gray-900)' }}>Escolha o horário</h2>
            </div>
            <p style={{ color:'var(--gray-500)', fontSize:14, marginBottom:16 }}>
              {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', { dateStyle:'full' })}
            </p>
            {slotsLoading ? (
              <div style={{ textAlign:'center', padding:32 }}><div className="spinner" /></div>
            ) : !slots.length ? (
              <div style={{ textAlign:'center', padding:32, color:'var(--gray-500)' }}>
                <div style={{ fontSize:36, marginBottom:8 }}>😔</div>
                <p>Nenhum horário disponível neste dia.</p>
                <button onClick={() => setStep(1)} style={{ background:'var(--primary)', color:'#fff', border:'none', borderRadius:8, padding:'8px 20px', cursor:'pointer', marginTop:8, fontSize:14, fontWeight:600 }}>Escolher outra data</button>
                {!showWaitlist && !wlDone && (
                  <div style={{ marginTop:20 }}>
                    <button onClick={() => setShowWaitlist(true)} style={{ background:'none', border:'1px solid var(--gray-300)', borderRadius:8, padding:'8px 20px', cursor:'pointer', fontSize:13, color:'var(--gray-600)' }}>
                      Entrar na lista de espera
                    </button>
                  </div>
                )}
                {showWaitlist && !wlDone && (
                  <form onSubmit={onJoinWaitlist} style={{ marginTop:20, textAlign:'left', background:'var(--gray-50)', borderRadius:12, padding:20 }}>
                    <h4 style={{ margin:'0 0 14px', fontSize:15 }}>Lista de espera</h4>
                    <div style={{ marginBottom:10 }}><input className="input" placeholder="Seu nome *" value={wlForm.client_name} onChange={e => setWlForm(f => ({ ...f, client_name: e.target.value }))} required /></div>
                    <div style={{ marginBottom:10 }}>
                      <PhoneInput
                        value={wlForm.client_phone}
                        onChange={(masked, normalized) => setWlForm(f => ({ ...f, client_phone: normalized || masked }))}
                        placeholder="Telefone / WhatsApp *"
                        required
                      />
                    </div>
                    <div style={{ marginBottom:10 }}><input className="input" type="email" placeholder="E-mail (opcional)" value={wlForm.client_email} onChange={e => setWlForm(f => ({ ...f, client_email: e.target.value }))} /></div>
                    <button type="submit" disabled={wlSubmitting} style={{ width:'100%', background:'var(--primary)', color:'#fff', border:'none', borderRadius:8, padding:'10px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                      {wlSubmitting ? 'Aguarde…' : 'Entrar na lista de espera'}
                    </button>
                  </form>
                )}
                {wlDone && <div style={{ marginTop:16, background:'#ecfdf5', borderRadius:10, padding:14, fontSize:14, color:'#065f46' }}>✅ Você foi adicionado à lista de espera! Avisaremos quando um horário ficar disponível.</div>}
              </div>
            ) : (
              <div>
                {(() => {
                  const groups = { 'Manhã ☀️': [], 'Tarde 🌤️': [], 'Noite 🌙': [] };
                  slots.forEach(t => {
                    const h = parseInt(t.split(':')[0]);
                    if (h < 12) groups['Manhã ☀️'].push(t);
                    else if (h < 18) groups['Tarde 🌤️'].push(t);
                    else groups['Noite 🌙'].push(t);
                  });
                  return Object.entries(groups).filter(([, s]) => s.length > 0).map(([label, groupSlots]) => (
                    <div key={label} style={{ marginBottom:16 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:'var(--gray-400)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:8 }}>{label}</div>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(80px,1fr))', gap:8 }}>
                        {groupSlots.map(t => (
                          <button key={t} onClick={() => selectTime(t)}
                            style={{ background:'var(--surface)', border:'2px solid var(--gray-200)', borderRadius:8, padding:'10px 4px', cursor:'pointer', fontWeight:600, fontSize:15, color:'var(--gray-800)', transition:'all .15s' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor='var(--primary)'; e.currentTarget.style.background='var(--gray-50)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor='var(--gray-200)'; e.currentTarget.style.background='var(--surface)'; }}>
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Dados do cliente */}
        {step === 3 && (
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
              <button onClick={() => setStep(2)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:'var(--gray-500)' }}>←</button>
              <h2 style={{ margin:0, fontSize:18, color:'var(--gray-900)' }}>Seus dados</h2>
            </div>
            <div className="info-box" style={{ marginBottom:20, fontSize:13 }}>
              {selectedSvc && <span>🛎️ <strong>{selectedSvc.name}</strong> · </span>}
              📅 {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', { dateStyle:'short' })} às <strong>{selectedTime}</strong>
            </div>
            <form onSubmit={onSubmit}>
              <div className="form-group" style={{ marginBottom:16 }}>
                <label style={{ display:'block', marginBottom:6, fontWeight:600, fontSize:14, color:'var(--gray-700)' }}>Nome completo *</label>
                <input name="client_name" value={form.client_name} onChange={onChange} required
                  placeholder="Seu nome completo"
                  className="input"
                  style={{ width:'100%', boxSizing:'border-box' }} />
              </div>
              <div className="form-group" style={{ marginBottom:16 }}>
                <label style={{ display:'block', marginBottom:6, fontWeight:600, fontSize:14, color:'var(--gray-700)' }}>Email</label>
                <input name="client_email" type="email" value={form.client_email} onChange={onChange}
                  placeholder="seu@email.com (para receber confirmação)"
                  className="input"
                  style={{ width:'100%', boxSizing:'border-box' }} />
              </div>
              <div className="form-group" style={{ marginBottom:16 }}>
                <label style={{ display:'block', marginBottom:6, fontWeight:600, fontSize:14, color:'var(--gray-700)' }}>Telefone / WhatsApp</label>
                <PhoneInput
                  name="client_contact"
                  value={form.client_contact}
                  onChange={(masked, normalized) => setForm(f => ({ ...f, client_contact: normalized || masked }))}
                  placeholder="(11) 91234-5678"
                  style={{ width:'100%', boxSizing:'border-box' }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: intakeFields.length ? 16 : 24 }}>
                <label style={{ display:'block', marginBottom:6, fontWeight:600, fontSize:14, color:'var(--gray-700)' }}>Observações</label>
                <textarea name="notes" value={form.notes} onChange={onChange} rows={3}
                  placeholder="Alguma informação adicional?"
                  className="input"
                  style={{ width:'100%', boxSizing:'border-box', resize:'vertical' }} />
              </div>
              {intakeFields.length > 0 && (
                <div style={{ marginBottom:24 }}>
                  <div style={{ borderTop:'1px solid var(--gray-200)', paddingTop:16, marginBottom:16 }}>
                    <span style={{ fontSize:13, fontWeight:600, color:'var(--gray-600)' }}>Informações adicionais</span>
                  </div>
                  {intakeFields.map(field => (
                    <div key={field.id} className="form-group" style={{ marginBottom:14 }}>
                      <label style={{ display:'block', marginBottom:6, fontWeight:600, fontSize:14, color:'var(--gray-700)' }}>
                        {field.label}{field.required ? ' *' : ''}
                      </label>
                      {field.type === 'textarea' ? (
                        <textarea className="input" rows={3} style={{ width:'100%', boxSizing:'border-box' }}
                          value={intakeAnswers[field.id] || ''} onChange={e => setIntakeAnswers(a => ({ ...a, [field.id]: e.target.value }))}
                          required={!!field.required} />
                      ) : field.type === 'select' ? (
                        <select className="input" style={{ width:'100%' }} value={intakeAnswers[field.id] || ''}
                          onChange={e => setIntakeAnswers(a => ({ ...a, [field.id]: e.target.value }))} required={!!field.required}>
                          <option value="">Selecione…</option>
                          {(safeParseJSON(field.options, [])).map((opt, i) => (
                            <option key={i} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : field.type === 'checkbox' ? (
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <input type="checkbox" checked={!!intakeAnswers[field.id]}
                            onChange={e => setIntakeAnswers(a => ({ ...a, [field.id]: e.target.checked ? 'sim' : '' }))} />
                          <span style={{ fontSize:14 }}>{field.label}</span>
                        </div>
                      ) : (
                        <input className="input" type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                          style={{ width:'100%', boxSizing:'border-box' }}
                          value={intakeAnswers[field.id] || ''} onChange={e => setIntakeAnswers(a => ({ ...a, [field.id]: e.target.value }))}
                          required={!!field.required} />
                      )}
                    </div>
                  ))}
                </div>
              )}
              <button type="submit" disabled={submitting}
                style={{ width:'100%', background:'var(--primary)', color:'#fff', border:'none', borderRadius:10, padding:'14px', fontSize:16, fontWeight:700, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? .7 : 1 }}>
                {submitting ? 'Confirmando…' : '✅ Confirmar Agendamento'}
              </button>
            </form>
          </div>
        )}
      </div>

      {!tenant?.whitelabel && (
        <div style={{ textAlign:'center', padding:'24px 16px', color:'var(--gray-400)', fontSize:12 }}>
          Powered by <strong style={{ color:'var(--gray-500)' }}>AtendoApp</strong> · Sistema de Agendamentos
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8, fontSize:14 }}>
      <span style={{ color:'var(--gray-500)' }}>{label}</span>
      <span style={{ fontWeight:600, color:'var(--gray-900)' }}>{value}</span>
    </div>
  );
}
